// src/lib/scope.ts — GM-TXT-02 B3: the worker-facing scope line ("What the worker sees").
// The same guards as the database (service_catalog_scope_*_chk + ops_validate_change), checked as you type so the
// preview never offers a change the database will refuse. The preview is the engine's real offer text
// (lib/dispatch is a generated copy of supabase/functions/_shared — scripts/sync-dispatch-templates_v1.sh).
import { render } from "./dispatch/dispatch-templates";
import type { Lang } from "./dispatch/dispatch-time";
import type { Service } from "./ops";

export const SCOPE_MAX = 220;
const BANNED = /(pago|salario|asignad|margen)/i;

/** A line starting with "_" is a Claude draft: workers never see it until a person removes the mark. */
export const isDraft = (s: string | null | undefined): boolean => !!s && s.trimStart().startsWith("_");
export const withoutMark = (s: string): string => s.replace(/^\s*_\s*/, "");

export function scopeProblems(s: string): string[] {
  const out: string[] = [];
  if (s.length > SCOPE_MAX) out.push(`${s.length - SCOPE_MAX} characters over the ${SCOPE_MAX} limit`);
  if (s.includes("$")) out.push("No prices — remove the $");
  const m = s.match(BANNED);
  if (m) out.push(`“${m[0]}” can't appear in worker texts (contractor rules)`);
  return out;
}

export type ScopeState = "missing" | "draft" | "verified";
export function scopeState(s: Pick<Service, "scope_es" | "scope_en">): ScopeState {
  if (!s.scope_es && !s.scope_en) return "missing";
  return isDraft(s.scope_es) || isDraft(s.scope_en) ? "draft" : "verified";
}

const money = (c: number | null | undefined) => (c == null ? null : `$${Math.round(c / 100).toLocaleString("en-US")}`);
const STOP: Record<Lang, string> = { es_MX: " Responde STOP para salir.", es_CA: " Respondé STOP para salir.", en: " Reply STOP to opt out." };

/** The offer exactly as the engine would send it (sample date, Everett 98203), with the sender's prefix and stop line. */
export function offerPreview(s: Pick<Service, "name" | "name_es" | "obra_rate_cents">, lang: Lang, scope: string): string {
  const en = lang === "en";
  const name = en ? s.name : s.name_es || s.name;
  const body = render("offer", lang, {
    job: name, short: name.split(" — ")[0], day: en ? "Thu Oct 22" : "jue 22 oct", Day: en ? "Thu Oct 22" : "Jue 22 oct",
    city: "Everett", zip: "98203", amount: money(s.obra_rate_cents), window: "8 am–5 pm",
    scope: withoutMark(scope).trim() || undefined,
  });
  return `Green Maestro: ${body}${STOP[lang]}`;
}

// SMS length: GSM-7 (160 / 153 per part) unless any character needs UCS-2 (70 / 67) — emoji and most accents do.
const GSM = "@£$¥èéùìòÇ\nØø\rÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ !\"#¤%&'()*+,-./0123456789:;<=>?¡ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÑÜ§¿abcdefghijklmnopqrstuvwxyzäöñüà";
const GSM_EXT = "^{}\\[~]|€";
export function smsSegments(text: string): { encoding: "GSM-7" | "UCS-2"; units: number; segments: number } {
  const chars = Array.from(text);
  if (chars.every((c) => GSM.includes(c) || GSM_EXT.includes(c))) {
    const units = chars.reduce((n, c) => n + (GSM_EXT.includes(c) ? 2 : 1), 0);
    return { encoding: "GSM-7", units, segments: units <= 160 ? 1 : Math.ceil(units / 153) };
  }
  const units = text.length; // UTF-16 code units
  return { encoding: "UCS-2", units, segments: units <= 70 ? 1 : Math.ceil(units / 67) };
}
